import { useEffect, useState } from 'react';
import { Box, IconButton, Modal, Typography, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';
import Button from 'components/Common/Button';
import { useNavigate, useParams } from 'react-router-dom';
import { IoClose } from 'react-icons/io5';
import Grid from '@mui/material/Grid2';
import { MdOutlineEdit } from 'react-icons/md';
import Input from 'components/Common/Input';
import Select from 'components/Common/Select';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { LuDownload } from 'react-icons/lu';
import { useGetInteractionLog, useGetInteractionsLogs } from 'apis/business/agent/callLogs/hook';
import Spinner from 'components/Common/Spinner';
import MusicPlayer from './MusicPlayer';
import { Fragment } from 'react/jsx-runtime';
import {
  useCreateNote,
  useDeleteNote,
  useGetContact,
  useGetNotes,
  useUpdateContact,
  useUpdateNote
} from 'apis/business/agent/contact/hook';
import { formatDate } from 'utils/helperFunctions';
import { InteractionLogResponse } from 'apis/business/agent/callLogs/types';
import NotesIcon from 'assets/icons/NotesIcon';
import TelInput from 'components/Common/TelInput';

export default function CallLogForm() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { id: callLogId } = useParams();

  const [editingNote, setEditingNote] = useState<number | null>(null);
  const [selectedCallLog, setSelectedCallLog] = useState<InteractionLogResponse | undefined>(undefined);

  const { data: callLog, isLoading: isCallLogLoading } = useGetInteractionLog();
  const { data: contact, isLoading: isContactLoading } = useGetContact(Number(callLog?.contact_id));
  const { mutateAsync: updateContact } = useUpdateContact(Number(callLog?.contact_id));
  const { mutateAsync: createNote, isPending: isCreateNotePending } = useCreateNote(Number(callLog?.contact_id));
  const { mutateAsync: updateNote, isPending: isUpdateNotePending } = useUpdateNote(Number(callLog?.contact_id));
  const { mutateAsync: deleteNote } = useDeleteNote(Number(callLog?.contact_id));
  const { data: notes } = useGetNotes(Number(callLog?.contact_id));
  const { data: callLogs, isLoading: isCallLogsLoading } = useGetInteractionsLogs({
    'filter[contact_id_eq]': Number(callLog?.contact_id)
  });

  const { handleSubmit, control, setValue, reset } = useForm({
    values: {
      contact: {
        ...contact,
        call: callLogs?.data?.[0]?.id || ''
      },
      note: { description: '' }
    }
  });

  const onSubmit = async (data: any) => {
    await updateContact(data);
  };

  const handleCreateOrUpdateNote = async (data: any) => {
    if (editingNote) {
      updateNote({ id: editingNote, payload: data.note });
      setEditingNote(null);
      reset();
    } else {
      await createNote(data.note);
      reset();
    }
  };

  const handleEditClick = (note: any) => {
    setEditingNote(note.id);
    setValue('note.description', note.description);
  };

  const downloadTranscript = () => {
    const transcriptText = selectedCallLog?.interaction_journey
      ?.map(
        (item: any) =>
          `${item.role === 'assistant' ? 'Agent' : item.role === 'tool' ? 'Tool' : 'Human'}: ${item?.content}`
      )
      .join('\n');

    const blob = new Blob([String(transcriptText)], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'transcript.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleSelectCallLog = (id: number) => {
    setSelectedCallLog(callLogs?.data.find((item) => item.id === id));
  };

  useEffect(() => {
    setValue('contact.call', String(callLogs?.data.find((item) => item.id === Number(callLogId))?.id));
    setSelectedCallLog(callLogs?.data.find((item) => item.id === Number(callLogId)));
  }, [setValue, callLogs, callLogId]);

  const isLoading = isCallLogLoading || isContactLoading;

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Modal
      open={true}
      onClose={() => navigate(-1)}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={style}>
        <Box padding='16px 24px' display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6'>Call Details</Typography>
          <Box sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)}>
            <IoClose />
          </Box>
        </Box>
        <Box>
          <Grid container>
            <Grid size={6} overflow='auto' height='600px'>
              <Box
                border='1px solid #ECECEC'
                borderRadius='4px'
                marginX='8px'
                padding={2}
                component='form'
                onSubmit={handleSubmit(onSubmit)}
              >
                <Box display='flex' alignItems='center' flexWrap='wrap' gap={2}>
                  <Typography variant='body2' color='secondary' fontWeight='500' marginRight={0.5}>
                    Contact Details
                  </Typography>
                  <Box display='flex' alignItems='center' flexWrap='wrap'>
                    <Typography variant='body2' color='secondary' fontWeight='500' marginRight={0.5}>
                      Created At:
                    </Typography>
                    <Typography variant='body2'>{formatDate(contact?.created_at)}</Typography>
                  </Box>
                  {contact?.updated_by_name && (
                    <Box display='flex' alignItems='center' flexWrap='wrap'>
                      <Typography variant='body2' color='secondary' fontWeight='500' marginRight={0.5}>
                        Updated By-
                      </Typography>
                      <Typography variant='body2'>{contact?.updated_by_name}</Typography>
                    </Box>
                  )}
                </Box>
                <Grid container spacing={3} marginTop={3}>
                  <Grid size={6}>
                    <Input name='contact.name' label='Name' control={control} fullWidth />
                  </Grid>
                  <Grid size={6}>
                    <Input name='contact.email' label='Email' control={control} fullWidth type='email' />
                  </Grid>
                  <Grid size={6}>
                    <TelInput
                      name='contact.phone_number'
                      label='Phone Number'
                      control={control}
                      defaultCountry='US'
                      preferredCountries={['US', 'CA', 'IN']}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={6}>
                    <Select
                      name='contact.status'
                      label='Type'
                      defaultValue='potential'
                      control={control}
                      options={[
                        { value: 'lead', label: 'Lead' },
                        { value: 'existing_customer', label: 'Existing Customer' },
                        { value: 'spam', label: 'Spam' },
                        { value: 'vendor', label: 'Vendor' },
                        { value: 'potential', label: 'Potential' }
                      ]}
                    />
                  </Grid>
                </Grid>
                <Button
                  type='submit'
                  sx={{
                    display: 'block',
                    marginLeft: 'auto',
                    marginTop: '24px'
                  }}
                >
                  Save Changes
                </Button>
              </Box>
              {/* {callLog?.token_usage && (
                <Box border='1px solid #ECECEC' borderRadius='4px' marginX='8px' padding={2} marginTop={2}>
                  <Typography variant='body2' color='secondary' fontWeight='500'>
                    Token Usage Details
                  </Typography>
                  <Grid container spacing={3} marginTop={1}>
                    <Grid size={12}>
                      <Typography variant='body2' color='textPrimary'>
                        LLM Input Token: {callLog?.token_usage.llm_input_token}
                      </Typography>
                      <Typography variant='body2' color='textPrimary' marginTop={1}>
                        LLM Output Token: {callLog?.token_usage.llm_output_token}
                      </Typography>
                      <Typography variant='body2' color='textPrimary' marginTop={1}>
                        STT Audio Duration: {callLog?.token_usage.stt_audio_duration}
                      </Typography>
                      <Typography variant='body2' color='textPrimary' marginTop={1}>
                        TTS Characters Count: {callLog?.token_usage.tts_characters_count}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )} */}
              <Box border='1px solid #ECECEC' borderRadius='4px' marginX='8px' padding={2} marginTop={2}>
                <Typography fontSize='500' marginBottom={1}>
                  Notes
                </Typography>
                {notes?.data?.map((note) => (
                  <Box
                    key={note.id}
                    sx={{ padding: '8px 16px' }}
                    border='1px solid #ECECEC'
                    borderRadius='4px'
                    display='flex'
                    justifyContent='space-between'
                    my={1}
                  >
                    <Box display='flex' gap={2} alignItems='center'>
                      <NotesIcon />
                      <Box>
                        <Typography variant='body2' marginBottom='6px'>
                          {note.description}
                        </Typography>
                        <Box display='flex' alignItems='center' gap={1}>
                          <Typography fontSize='12px' color='secondary'>
                            {formatDate(note.created_at)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box display='flex' gap={1} alignItems='center'>
                      <IconButton onClick={() => handleEditClick(note)}>
                        <MdOutlineEdit />
                      </IconButton>
                      <IconButton onClick={() => deleteNote(note.id)} color='error'>
                        <RiDeleteBin6Line style={{ color: 'red' }} />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
                <Box component='form' onSubmit={handleSubmit(handleCreateOrUpdateNote)}>
                  <Input
                    multiline
                    minRows={4}
                    name='note.description'
                    label='Add a note'
                    fullWidth
                    control={control}
                    sx={{ marginTop: '24px' }}
                  />
                  <Button
                    type='submit'
                    fullWidth
                    sx={{ marginTop: '16px' }}
                    isPending={isCreateNotePending || isUpdateNotePending}
                  >
                    {editingNote ? 'Update Note' : 'Add Note'}
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid size={6} bgcolor={theme.palette.background.default} padding={2}>
              <Select
                name='contact.call'
                label='Calls'
                control={control}
                isLoading={isCallLogsLoading}
                options={
                  callLogs?.data?.map((item, index) => ({
                    label: item.pretty_name,
                    value: item.id
                    // selected: index === 0 ? true : false
                  })) ?? []
                }
                onChange={(e) => {
                  handleSelectCallLog(Number(e.target.value));
                  setValue('contact.call', Number(e.target.value));
                }}
                size='small'
                sx={{ width: '300px' }}
              />
              {callLog?.recording_url && <MusicPlayer url={callLog?.recording_url} />}
              <Typography color='primary' fontWeight='500' marginTop={1} marginBottom={1}>
                Call Summary
              </Typography>
              <Typography
                bgcolor={theme.palette.background.paper}
                borderRadius={1}
                border='1px solid #ECECEC'
                padding={1}
              >
                {callLog?.summary}
              </Typography>
              <Box display='flex' justifyContent='space-between' alignItems='center'>
                <Typography color='primary' fontWeight='500' marginTop={3} marginBottom={2}>
                  Transcript
                </Typography>
                <Button color='secondary' variant='text' onClick={() => downloadTranscript()}>
                  <LuDownload />
                  <Typography color='secondary' variant='body2' ml={1}>
                    Download Transcript
                  </Typography>
                </Button>
              </Box>
              <Box maxHeight={callLog?.recording_url ? 180 : 300} overflow='auto'>
                {selectedCallLog?.interaction_journey?.map((item, index) => (
                  <Fragment key={index}>
                    <Box display='flex' my={1} justifyContent={item.role !== 'user' ? 'end' : 'start'}>
                      <Box
                        border='1px solid #ECECEC'
                        sx={{ padding: '4px 10px' }}
                        bgcolor={theme.palette.background.paper}
                        borderRadius={1}
                        maxWidth='75%'
                      >
                        <Typography display='inline' fontWeight='500'>
                          {item.role === 'assistant' ? 'Agent:' : item.role === 'tool' ? 'Tool:' : 'Human:'}
                        </Typography>
                        <Typography display='inline'>{item?.content}</Typography>
                      </Box>
                    </Box>
                  </Fragment>
                ))}
              </Box>
            </Grid>
          </Grid>
          {/* TODO - @need to work later */}
          {/* <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              gap: '16px',
              padding: '16px 24px'
            }}>
            <Button type='submit' variant='contained' color='error' sx={{ gap: '8px' }}>
              <MdBlockFlipped />
              Permanently block
            </Button>
            <Button
              color='secondary'
              variant='outlined'
              sx={{ border: '1px solid #777777', gap: '8px' }}
              onClick={() => navigate(-1)}>
              <VscDebugStepOver />
              Add to direct transfer
            </Button>
          </Box> */}
        </Box>
      </Box>
    </Modal>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  border: '1px solid #F3F3F3',
  boxShadow: 24,
  borderRadius: '8px',
  width: {
    xs: '90%',
    sm: 400,
    md: 600,
    lg: 1000
  }
};
